/*
 * Copyright (c) 2023 Limpide
 *
 *  Permission is hereby granted, free of charge, to any person obtaining a copy of
 *  this software and associated documentation files (the "Software"), to deal in
 *  the Software without restriction, including without limitation the rights to
 *  use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 *  the Software, and to permit persons to whom the Software is furnished to do so,
 *  subject to the following conditions:
 *
 *  The above copyright notice and this permission notice shall be included in all
 *  copies or substantial portions of the Software.
 *
 *  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 *  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 *  FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 *  COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 *  IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 *  CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import Marquer from '@scripts/marquer'
import Banner from '@scripts/components/banner'
import BlockWrapper from '@scripts/components/block-wrapper'
import HeadingHomepage from '@scripts/components/heading-homepage'
import MessageHandler from '@scripts/components/message-handler'
import PostListing from '@scripts/components/post-listing'
import PreFooter from '@scripts/components/pre-footer'
import RelatedPosts from '@scripts/components/related-posts'
import UnderlinedText from '@scripts/components/underlined-text'
import Guide from '@scripts/guide'
import '@styles/main.scss'
import { register } from 'swiper/element/bundle'
import Popup from './components/popup'

console.log('[Application] bootstrap')

const { DEV } = import.meta.env

register()

const messageHandler: MessageHandler = new MessageHandler()
messageHandler.start()

document.addEventListener('DOMContentLoaded', () => {
    console.log('[Application] DOM loaded')

    /**
     * Enable guide in dev mode
     */
    DEV && new Guide().start()

    /**
     * Load components
     */
    const bannerElement: HTMLElement | null = document.querySelector('.banner')
    const popupElement: HTMLElement | null = document.querySelector('.popup')
    const headingHomepages: NodeListOf<HTMLElement> | null = document.querySelectorAll('.heading-homepage')
    const relatedPosts: NodeListOf<HTMLElement> | null = document.querySelectorAll('.related-posts')
    const underlinedText: NodeListOf<HTMLElement> | null = document.querySelectorAll('.underlined-text')
    const blockWrappers: NodeListOf<HTMLElement> | null = document.querySelectorAll('.block-wrapper')
    const postListingElement: HTMLElement | null = document.querySelector('.post-listing')
    const preFooterElement: HTMLElement | null = document.querySelector('.prefooter')
    const gestionCookies: HTMLElement | null = document.querySelector('.gestionCookies')

    if (bannerElement) {
        const banner: Banner = new Banner(bannerElement)

        banner.start()
    }

    if (popupElement) {
        const popup: Popup = new Popup(popupElement)

        popup.start()
    }

    if (postListingElement) {
        const postListing: PostListing = new PostListing(postListingElement)

        postListing.start()
    }

    if (preFooterElement) {
        const preFooter: PreFooter = new PreFooter(preFooterElement)

        preFooter.start()
    }

    if (window.layerData.data_layer) {
        const marquers: Marquer = new Marquer(window.layerData.data_layer)

        marquers.start()
    }

    headingHomepages?.forEach((element: HTMLElement): void => {
        const heading: HeadingHomepage = new HeadingHomepage(element)

        heading.start()
    })

    underlinedText?.forEach((element: HTMLElement): void => {
        const underlinedText: UnderlinedText = new UnderlinedText(element)

        underlinedText.start()
    })

    relatedPosts?.forEach((element: HTMLElement): void => {
        const relatedPosts: RelatedPosts = new RelatedPosts(element)

        relatedPosts.start()
    })

    blockWrappers?.forEach((element: HTMLElement): void => {
        const blockWrapper: BlockWrapper = new BlockWrapper(element)

        blockWrapper.start()
    })

    if (gestionCookies) {
        gestionCookies.addEventListener('click', event => {
            event.preventDefault()
            if (window.tC) {
                window.tC.privacyCenter.showPrivacyCenter()
            }
        })
    }

    /**
     * Add global event listener
     */
    window.addEventListener(
        'scroll',
        () => {
            const banner: HTMLElement | null = document.querySelector('header.banner')
            const breadcrumb: HTMLElement | null = document.querySelector('.breadcrumbs')
            const single: HTMLElement | null = document.querySelector('article.single')
            const headingCategory: HTMLElement | null = document.querySelector('.heading-category')

            document.body.style.setProperty(
                '--scroll',
                (
                    window.pageYOffset /
                    ((single && banner && breadcrumb
                        ? breadcrumb?.offsetHeight + banner?.offsetHeight + single.offsetHeight
                        : document.body.offsetHeight) -
                        window.innerHeight)
                ).toString()
            )

            if (headingCategory instanceof HTMLElement) {
                document.body.style.setProperty(
                    '--scroll-heading',
                    (
                        window.pageYOffset /
                        ((headingCategory && banner
                            ? banner?.offsetHeight + headingCategory.offsetHeight
                            : document.body.offsetHeight) -
                            window.innerHeight / 2)
                    ).toString()
                )
            }
        },
        false
    )
})
